import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb, Send, Loader2, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * SuggestionForm
 *
 * Renders a collapsible "Suggest a feature" card on the customer + business
 * back-office dashboards. Submissions go to `POST /me/suggestions` which both
 * stores them and forwards the payload to `SUPPORT_EMAIL` via Resend.
 *
 * Also lists the user's past suggestions with their current admin-assigned
 * status (new / planned / shipped / declined) so they can see follow-ups.
 */
const CATEGORIES = [
  { v: "feature", label: "New feature" },
  { v: "improvement", label: "Improvement" },
  { v: "bug", label: "Bug or problem" },
  { v: "other", label: "Something else" },
];

const STATUS_STYLES = {
  new: { label: "Received", cls: "bg-gray-100 text-gray-700 border-gray-200" },
  planned: { label: "Planned", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  shipped: { label: "Shipped", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  declined: { label: "Declined", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

export default function SuggestionForm() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("feature");
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loadedHistory, setLoadedHistory] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/me/suggestions");
      setItems(data.items || []);
    } catch {
      /* ignore — endpoint only fails for non-signed-in, which shouldn't happen here */
    } finally {
      setLoadedHistory(true);
    }
  };

  useEffect(() => {
    if (open && !loadedHistory) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3 || body.trim().length < 10) {
      toast.error("Title needs ≥3 characters and description ≥10 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/me/suggestions", { title: title.trim(), body: body.trim(), category });
      toast.success("Thanks! Your suggestion was sent to the Servrevu team.");
      setTitle("");
      setBody("");
      setCategory("feature");
      await loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <Card className="rounded-md border border-border p-6" data-testid="suggestion-form-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <Lightbulb size={18} />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-xl">Suggest a feature or improvement</h3>
              <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
                Tell us what would make Servrevu better for you. Every submission
                goes straight to our support inbox and we reply personally.
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
            data-testid="suggestion-form-open"
          >
            <Sparkles size={14} className="mr-1.5" /> Share an idea
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="rounded-md border border-border p-6" data-testid="suggestion-form-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Lightbulb size={16} className="text-amber-600" />
            <h3 className="font-display text-xl">Suggest a feature or improvement</h3>
            {items.length > 0 && (
              <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-[#B31E1E]/40 text-[#B31E1E]" data-testid="suggestion-count-badge">
                {items.length} submitted
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Direct line to the Servrevu team. Every idea is emailed to support and
            triaged into <em>Planned</em>, <em>Shipped</em>, or <em>Declined</em>.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          className="rounded-[35px] shadow-sm"
          data-testid="suggestion-form-close"
        >
          Close
        </Button>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="suggestion-form">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder="e.g. Add gift cards to the loyalty program"
              className="rounded-md mt-1"
              data-testid="suggestion-title-input"
              required
            />
          </div>
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-md mt-1" data-testid="suggestion-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Description</Label>
          <Textarea
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            placeholder="Describe the problem you're trying to solve, or the feature you'd like. The more detail, the better — screenshots, examples from other apps, all welcome."
            className="rounded-md mt-1"
            data-testid="suggestion-body-input"
            required
          />
          <p className="text-[11px] font-mono text-gray-400 mt-1">{body.length}/4000 characters</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="submit"
            disabled={submitting}
            className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
            data-testid="suggestion-submit"
          >
            {submitting ? (
              <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…</>
            ) : (
              <><Send size={14} className="mr-1.5" /> Send to Servrevu support</>
            )}
          </Button>
          <span className="text-[11px] font-mono text-gray-400">Rate limit: 10 per 24 h.</span>
        </div>
      </form>

      {items.length > 0 && (
        <div className="mt-6" data-testid="suggestion-history">
          <div className="text-[11px] font-mono uppercase tracking-wider text-gray-500 mb-2">Your submissions</div>
          <div className="border border-border rounded-md divide-y divide-border bg-white">
            {items.map((s) => {
              const status = STATUS_STYLES[s.status] || STATUS_STYLES.new;
              return (
                <div key={s.id} className="p-3" data-testid={`suggestion-item-${s.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{s.title}</div>
                      <div className="text-[11px] font-mono text-gray-400 mt-0.5">
                        {new Date(s.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} · {s.category}
                      </div>
                    </div>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono border ${status.cls}`}>{status.label}</span>
                  </div>
                  {s.admin_note && (
                    <div className="mt-2 text-xs text-gray-700 bg-blue-50 border border-blue-100 rounded px-2 py-1.5">
                      <b className="text-blue-800">Team note:</b> {s.admin_note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
